/**
 * Theme is session state, not server state — Zustand, not TanStack Query.
 * See ARCHITECTURE §6 for the state-ownership rules.
 *
 * Dark is the primary theme; light exists for printing and daylight offices.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHO OWNS WHICH AXIS, WHICH IS NOT THE SAME ANSWER FOR ALL THREE
 * ─────────────────────────────────────────────────────────────────────────────
 *
 *   Theme    light / dark        THE PERSON.       Per browser, per person, kept here.
 *   Palette  which neutral       THE ORGANISATION. Column on `organisations`, migration 0058.
 *   Accent   the brand hue       THE ORGANISATION. Same.
 *
 * The split is the difference between comfort and branding. Light-or-dark changes with the hour,
 * the room and the person, and nobody's employer has an opinion about it worth enforcing. The
 * palette and the accent are what the console LOOKS like — a tenant's people should all see the
 * tenant's look, rather than whichever one of them opened a picker last, and a platform operator
 * that has branded a customer's console should not have it undone one seat at a time.
 *
 * So the last two arrive from `GET /me`, already resolved (never null), and this store APPLIES
 * them. It does not choose them and there is no picker for them in the header any more: the
 * choice is made on the organisation, by an ancestor, in Access → the organisation drawer →
 * Appearance. Two controls for one fact is how they come to disagree.
 *
 * What survives here is `localStorage`, demoted from a preference to a HINT — see
 * `initialPalette()`.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * TWO AXES, AND `Theme` IS DELIBERATELY NOT ONE OF THEM
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * A palette is a second, ORTHOGONAL choice: `Theme` says light-or-dark, `Palette` says which
 * neutral the surfaces are mixed from. They multiply — four palettes x two modes — and
 * `tokens.css` resolves them by naming both attributes in one selector.
 *
 * THE OBVIOUS SHAPE WOULD HAVE BROKEN FIVE CALLERS SILENTLY. Widening `Theme` to
 * `'light' | 'dark' | 'slate' | …` is the first thing that comes to mind and it is wrong:
 *
 *   Chart.tsx, TimeSeriesChart.tsx, BucketedSeriesChart.tsx   ask `theme === 'dark'` to pick a
 *                                                             Highcharts palette
 *   FleetMap.tsx                                              keys `Record<Theme, string>` of
 *                                                             Mapbox style URLs off it
 *
 * Under a widened union the three chart comparisons quietly evaluate false — charts render
 * their LIGHT palette on a dark page, nothing throws, and it is only visible on screens that
 * happen to have a chart. The map would have failed louder (a type error demanding six style
 * URLs), which is the only reason that one would have been caught.
 *
 * So `Theme` keeps its meaning and its two values, every one of those callers is untouched,
 * and the palette travels beside it. The seam is that nothing outside this file and the
 * picker ever needs to know a palette exists.
 */
import { create } from 'zustand';

export type Theme = 'light' | 'dark';

/**
 * The neutral the chrome is mixed from. `thunder` is the original palette and has no block in
 * `tokens.css` at all — it IS the `:root` and `[data-theme]` blocks, so selecting it means
 * matching no palette selector and inheriting what was always there.
 *
 * THE VOCABULARY EXISTS FOUR TIMES AND THIS IS THE ONE THE OTHERS FOLLOW: the `org_palette` pg
 * enum (0058), the zod schema built from that enum's `enumValues`, the `[data-palette]` blocks in
 * `tokens.css`, and here. Adding one means all four in the same edit — the type error in
 * `lib/api/tenancy.ts` is the thing that will tell you, because it imports this union rather than
 * retyping it.
 */
export type Palette = 'thunder' | 'slate' | 'stone' | 'zinc';

/** For the organisation's Appearance tab, in the order it should offer them. */
export const PALETTES: ReadonlyArray<{ id: Palette; label: string }> = [
  { id: 'thunder', label: 'Thunder' },
  { id: 'slate', label: 'Slate' },
  { id: 'stone', label: 'Stone' },
  { id: 'zinc', label: 'Zinc' },
];

/**
 * The brand hue — a THIRD axis, orthogonal to both of the others.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY IT IS NOT A PROPERTY OF THE PALETTE
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * The obvious shape is to let each palette carry its own accent — slate is orange, stone is
 * orange, thunder is lime. It is wrong for the same reason the mode is not a palette: the two
 * answer unrelated questions. "Which neutral are the surfaces mixed from" and "what colour is
 * this company" have nothing to do with each other, and coupling them would mean an operator
 * who wants warm greys is forced into a brand hue they did not choose, or vice versa.
 *
 * So they multiply: 4 palettes x 2 modes x 6 accents. `contrast.test.ts` measures every one of
 * the forty-eight, because `--brand`'s legibility depends on the ground it lands on and the
 * ground is the palette's.
 *
 * ── THE ORDER IS BY CLEARANCE, NOT BY TASTE ──────────────────────────────────
 * Ordered roughly by how much room each hue has before it collides with something that MEANS
 * something. `lime` (97° clear) and `violet` (65°) are unambiguous; `orange` (12° from danger)
 * and `teal` (14°, between success and path-b) are compromises that `tokens.css` names in full.
 * An operator picking from this list is picking how distinguishable their brand is from a fault,
 * and the order is the only place the UI can say so without a paragraph in a menu.
 *
 * ── THE ACCENT OWNS `--brand*` AND NOTHING ELSE ──────────────────────────────
 * Not `--success`, not `--warn`, not `--path-a`/`--path-b`, not `--sld-*`. Those carry meaning
 * — a verdict, a redundant path, a bus type — and an accent that re-hued them would be
 * changing what the console SAYS, not how it looks. `--ring` follows `--brand` and always has;
 * that is the one deliberate dependency, so a focus ring is visibly the product's colour.
 */
export type Accent = 'lime' | 'orange' | 'violet' | 'citron' | 'magenta' | 'teal';

/**
 * `lime` is the original and has no block in `tokens.css` at all — selecting it means matching
 * no accent selector and inheriting the brand the mode blocks already define.
 */
export const ACCENTS: ReadonlyArray<{ id: Accent; label: string }> = [
  { id: 'lime', label: 'Lime' },
  { id: 'citron', label: 'Citron' },
  { id: 'teal', label: 'Teal' },
  { id: 'violet', label: 'Violet' },
  { id: 'magenta', label: 'Magenta' },
  { id: 'orange', label: 'Orange' },
];

const KEY = 'lumen_theme';
const PALETTE_KEY = 'lumen_palette';
const ACCENT_KEY = 'lumen_accent';

function initial(): Theme {
  try {
    const stored = localStorage.getItem(KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch {
    /* private window, blocked storage — fall through */
  }
  return window.matchMedia?.('(prefers-color-scheme: light)').matches
    ? 'light'
    : 'dark';
}

/**
 * ─────────────────────────────────────────────────────────────────────────────
 * THE STORED PALETTE AND ACCENT ARE A HINT FOR ONE PAINT, NOT A PREFERENCE
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * The console renders before `/me` answers. Without a seed it paints in thunder/lime and then
 * repaints in the organisation's look a few hundred milliseconds later — a visible flash of a
 * brand that is not yours, on every single page load, which is exactly the thing branding exists
 * to avoid.
 *
 * So the last look `/me` reported is written to `localStorage` and read back here as the FIRST
 * paint's guess. The distinction from a preference matters and is the whole reason these two
 * functions still exist:
 *
 *   · nothing the PERSON does writes these keys any more — only `applyOrganisation`, from the
 *     server's answer
 *   · a stale hint is corrected the moment `/me` lands, and is never consulted again
 *   · so a wrong value costs one frame, never a wrong console
 *
 * Which is why the fallbacks below must agree with the SERVER's `DEFAULT_PALETTE`/`DEFAULT_ACCENT`
 * and are still not a second copy of them: they answer "what to draw before we have asked", and
 * the server answers "what this organisation actually looks like". If the platform re-brands, the
 * server moves and every console follows on its first `/me`; only the pre-answer frame lags.
 */
function initialAccent(): Accent {
  try {
    const stored = localStorage.getItem(ACCENT_KEY);
    if (ACCENTS.some((a) => a.id === stored)) return stored as Accent;
  } catch {
    /* private window, blocked storage — fall through */
  }
  return 'lime';
}

function initialPalette(): Palette {
  try {
    const stored = localStorage.getItem(PALETTE_KEY);
    if (PALETTES.some((p) => p.id === stored)) return stored as Palette;
  } catch {
    /* private window, blocked storage — fall through */
  }
  return 'thunder';
}

/**
 * Write both attributes, always together.
 *
 * `data-palette` is written even for `thunder`, which matches no selector in `tokens.css`. It
 * costs nothing and it means the DOM always states the full answer — someone inspecting a
 * misrendered page sees `thunder` rather than an absent attribute they have to know the
 * meaning of.
 */
function apply(theme: Theme, palette: Palette, accent: Accent): void {
  const root = document.documentElement;
  root.setAttribute('data-theme', theme);
  root.setAttribute('data-palette', palette);
  root.setAttribute('data-accent', accent);
  try {
    /* `KEY` is the person's preference and is read back as one. The other two are the HINT — see
       `initialPalette()`. Written here rather than in `applyOrganisation` so the three keys can
       never disagree with the three attributes: one function writes both. */
    localStorage.setItem(KEY, theme);
    localStorage.setItem(PALETTE_KEY, palette);
    localStorage.setItem(ACCENT_KEY, accent);
  } catch {
    /* nothing to do — the attributes are already set */
  }
}

interface ThemeState {
  /** The PERSON's, and the only one they choose. */
  theme: Theme;
  /** The ORGANISATION's, applied from `/me`. */
  palette: Palette;
  /** The ORGANISATION's, applied from `/me`. */
  accent: Accent;
  toggle: () => void;
  set: (t: Theme) => void;
  /**
   * Paint what the server says this organisation looks like.
   *
   * Takes BOTH together because they arrive together and are written to the DOM together, and a
   * two-call version would repaint twice for one answer. Idempotent by design: `useMe` is read
   * from a dozen components and every one of them calls this, so it must be free to be called
   * with the value already in the store — the equality check below is what makes that true, and
   * without it Zustand would notify every subscriber on every render of every consumer.
   *
   * There is deliberately NO `setPalette`/`setAccent` any more. A person cannot choose these; an
   * ancestor organisation does, through `PATCH /organisations/:id`. A local setter would be a
   * second authority over one fact, and the local one always wins by being nearer — which is how
   * a tenant would quietly un-brand themselves and nobody would know why.
   */
  applyOrganisation: (palette: Palette, accent: Accent) => void;
}

export const useTheme = create<ThemeState>((set, get) => {
  const theme = initial();
  const palette = initialPalette();
  const accent = initialAccent();
  apply(theme, palette, accent);
  return {
    theme,
    palette,
    accent,
    toggle: () => {
      const next: Theme = get().theme === 'dark' ? 'light' : 'dark';
      apply(next, get().palette, get().accent);
      set({ theme: next });
    },
    set: (t) => {
      apply(t, get().palette, get().accent);
      set({ theme: t });
    },
    applyOrganisation: (p, a) => {
      const cur = get();
      if (cur.palette === p && cur.accent === a) return;
      apply(cur.theme, p, a);
      set({ palette: p, accent: a });
    },
  };
});

interface NavState {
  collapsed: boolean;
  toggle: () => void;
  /**
   * Which nav groups are expanded, by label.
   *
   * ─────────────────────────────────────────────────────────────────────────────
   * WHY A SET OF OPEN GROUPS AND NOT A SINGLE ACTIVE ONE
   * ─────────────────────────────────────────────────────────────────────────────
   *
   * A true one-at-a-time drilldown closes what you were looking at every time you open
   * something else, and with five groups an operator comparing Integrations against
   * Telemetry would be re-opening a group on every switch. So several may be open, and the
   * DEFAULT is "only the group containing the current page" — which is the drilldown
   * behaviour where it matters (arriving somewhere) without the cost where it does not
   * (working across two areas).
   *
   * `null` means the store has not seeded yet, and `Shell` seeds it from the active tab on
   * first render. Distinguishing "not seeded" from "everything closed" matters: an operator
   * who deliberately collapses every group must not have that undone on the next render.
   */
  open: ReadonlySet<string> | null;
  toggleGroup: (label: string) => void;
  /** Seed on first render, from whichever group holds the active page. */
  seedGroups: (labels: readonly string[]) => void;
}

const NAV_KEY = 'lumen_nav_groups';

function storedGroups(): ReadonlySet<string> | null {
  try {
    const raw = localStorage.getItem(NAV_KEY);
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.every((x) => typeof x === 'string')) {
      return new Set(parsed);
    }
  } catch {
    /* private window, blocked storage, or a value from an older shape — reseed */
  }
  return null;
}

function persistGroups(open: ReadonlySet<string>): void {
  try {
    localStorage.setItem(NAV_KEY, JSON.stringify([...open]));
  } catch {
    /* the rail is already correct in memory; persistence is a convenience */
  }
}

/**
 * NARROW SCREENS START WITH THE RAIL COLLAPSED. Expanded, the rail is 238 px — on a 390 px phone
 * that left 152 px for the page (measured 2026-09-29): every card squeezed to a column of
 * truncated words. Below the `md` breakpoint it starts at icon width, and crossing the breakpoint
 * (rotating a tablet, resizing a window) moves it with the screen; the toggle still overrides.
 */
const NARROW_QUERY = '(max-width: 767px)';

function isNarrow(): boolean {
  try {
    return window.matchMedia(NARROW_QUERY).matches;
  } catch {
    return false;
  }
}

export const useNav = create<NavState>((set, get) => ({
  collapsed: isNarrow(),
  toggle: () => set({ collapsed: !get().collapsed }),

  open: storedGroups(),

  toggleGroup: (label) => {
    const current = get().open ?? new Set<string>();
    const next = new Set(current);
    if (next.has(label)) next.delete(label);
    else next.add(label);
    persistGroups(next);
    set({ open: next });
  },

  /*
   * Only ever seeds — never overrides. If storage already holds a choice, that choice is
   * the operator's and this is a no-op.
   */
  seedGroups: (labels) => {
    if (get().open !== null) return;
    const next = new Set(labels);
    persistGroups(next);
    set({ open: next });
  },
}));

try {
  window.matchMedia(NARROW_QUERY).addEventListener('change', (e) => useNav.setState({ collapsed: e.matches }));
} catch {
  /* no matchMedia (tests, very old browsers): the rail keeps whatever state it has */
}
